import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User } from '../types';
import { supabase } from '../lib/supabase';
import { navigatePromise } from '../router';

interface AuthContextType {
  user: User | null;
  coupleData: any | null;
  loading: boolean;
  inviteCode: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, inviteCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshCoupleData: (userId?: string) => Promise<void>;
  setInviteCode: (code: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [coupleData, setCoupleData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCodeState] = useState<string | null>(null);
  const initialCheckDone = useRef(false);
  const isSigningUp = useRef(false);

  const fetchCoupleData = async (userId: string) => {
    try {
      const { data: allRecords, error: fetchError } = await supabase
        .from('couples')
        .select('*')
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      if (!allRecords || allRecords.length === 0) {
        setCoupleData(null);
        return null;
      }

      let selectedRecord = allRecords[0];

      if (allRecords.length > 1) {
        const completeRecord = allRecords.find(r => r.user1_id && r.user2_id);
        if (completeRecord) {
          selectedRecord = completeRecord;
        } else {
          selectedRecord = allRecords[0];
        }

        const recordsToDelete = allRecords
          .filter(r => r.id !== selectedRecord.id)
          .map(r => r.id);

        if (recordsToDelete.length > 0) {
          await supabase
            .from('couples')
            .delete()
            .in('id', recordsToDelete);
        }
      }

      // Auto-revogar Premium de código promo expirado
      if (
        selectedRecord.is_premium &&
        selectedRecord.premium_expires_at &&
        !selectedRecord.stripe_subscription_id &&
        new Date(selectedRecord.premium_expires_at) < new Date()
      ) {
        await supabase
          .from('couples')
          .update({ is_premium: false, premium_expires_at: null, premium_plan: null })
          .eq('id', selectedRecord.id);
        selectedRecord = { ...selectedRecord, is_premium: false, premium_expires_at: null, premium_plan: null };
      }

      setCoupleData(selectedRecord);
      return selectedRecord;
    } catch (error: any) {
      const isNetworkError = error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError');
      if (!isNetworkError) {
        console.error('Erro ao buscar dados do casal:', error);
      }
      return null;
    }
  };

  const refreshCoupleData = async (userId?: string) => {
    const id = userId || user?.id;
    if (id) {
      await fetchCoupleData(id);
    }
  };

  const setInviteCode = (code: string | null) => {
    setInviteCodeState(code);
  };

  useEffect(() => {
    let mounted = true;

    const navigateTo = (path: string) => {
      navigatePromise.then(nav => {
        if (mounted) nav(path);
      });
    };

    // Verificar sessão ativa apenas uma vez
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;

      if (session?.user) {
        const userData: User = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name,
          avatar_url: session.user.user_metadata?.avatar_url,
        };
        setUser(userData);
        fetchCoupleData(session.user.id).then((couple) => {
          if (!mounted) return;
          setLoading(false);
          initialCheckDone.current = true;

          // Redirecionar apenas se estiver em página pública
          const rawPath = window.location.pathname;
          const base = __BASE_PATH__ ? __BASE_PATH__.replace(/\/$/, '') : '';
          const relativePath = rawPath.startsWith(base) ? rawPath.slice(base.length) : rawPath;
          const cleanPath = relativePath || '/';
          const isPublicPage = cleanPath === '/' || cleanPath === '/login' || cleanPath === '/cadastro';

          if (isPublicPage) {
            navigateTo(couple ? '/dashboard' : '/onboarding');
          }
        });
      } else {
        setLoading(false);
        initialCheckDone.current = true;
      }
    });

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!mounted) return;

        if (session?.user) {
          const userData: User = {
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.name,
            avatar_url: session.user.user_metadata?.avatar_url,
          };
          setUser(userData);

          if (_event === 'SIGNED_IN') {
            // Se veio do signup, o próprio signup já navega — não redirecionar aqui
            if (isSigningUp.current) return;

            const couple = await fetchCoupleData(session.user.id);
            if (!mounted) return;
            setLoading(false);

            navigatePromise.then(nav => {
              if (!mounted) return;
              setTimeout(() => {
                nav(couple ? '/dashboard' : '/onboarding');
              }, 150);
            });
          } else if (_event === 'TOKEN_REFRESHED' || _event === 'INITIAL_SESSION') {
            if (!initialCheckDone.current) {
              await fetchCoupleData(session.user.id);
              if (mounted) setLoading(false);
            }
          }
        } else if (_event === 'SIGNED_OUT') {
          setUser(null);
          setCoupleData(null);
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        const userData: User = {
          id: data.user.id,
          email: data.user.email || '',
          name: data.user.user_metadata?.name,
          avatar_url: data.user.user_metadata?.avatar_url,
        };
        setUser(userData);
        await fetchCoupleData(data.user.id);
      }
    } catch (error: any) {
      console.error('Erro no login:', error);
      throw error;
    }
  };

  const signup = async (email: string, password: string, providedInviteCode?: string) => {
    try {
      isSigningUp.current = true;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        isSigningUp.current = false;
        throw error;
      }

      if (data.user) {
        const userData: User = {
          id: data.user.id,
          email: data.user.email || '',
          name: data.user.user_metadata?.name,
          avatar_url: data.user.user_metadata?.avatar_url,
        };
        setUser(userData);

        if (providedInviteCode) {
          setInviteCodeState(providedInviteCode);
        }

        // Navegar diretamente para onboarding
        navigatePromise.then(nav => nav('/onboarding'));
      }
    } catch (error: any) {
      isSigningUp.current = false;
      console.error('Erro no cadastro:', error);
      throw error;
    } finally {
      // Liberar a flag após um curto delay para o onAuthStateChange já ter passado
      setTimeout(() => {
        isSigningUp.current = false;
      }, 1000);
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setCoupleData(null);
      setInviteCodeState(null);
      navigatePromise.then(nav => nav('/login'));
    } catch (error) {
      console.error('Erro no logout:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        coupleData,
        loading,
        inviteCode,
        login,
        signup,
        logout,
        refreshCoupleData,
        setInviteCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
